import React, { useEffect, useState, useRef } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { logError } from "../lib/logger";

const KNOWN_SYMBOLS = ["XAUUSD", "XAUEUR", "BTCUSD", "ETHUSD", "EURUSD", "USDJPY", "EURGBP", "GBPUSD"];

function resolveSymbol(n) {
  if (n.symbol && KNOWN_SYMBOLS.includes(n.symbol)) return n.symbol;
  const text = `${n.title || ""} ${n.body || ""}`.toUpperCase();
  return KNOWN_SYMBOLS.find((s) => text.includes(s)) || null;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) {
      logError("Errore caricamento notifiche:", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/notifications/read-all");
      setUnread(0);
    }
  };

  const openTrade = (n) => {
    const sym = resolveSymbol(n);
    if (!sym) return;
    setOpen(false);
    navigate(`/markets?symbol=${encodeURIComponent(sym)}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={toggle}
        className="relative p-2 rounded-md hover:bg-white/5 transition-colors">
        <Bell size={20} className="text-zinc-300" />
        {unread > 0 && (
          <span data-testid="notification-unread-badge"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-mono pulse-dot">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-[#141414] border border-white/10 rounded-md shadow-2xl z-50">
          <div className="px-4 py-3 border-b border-white/10 text-sm font-semibold text-white">Notifiche</div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-500">Nessuna notifica</div>
          ) : items.map((n) => {
            const sym = resolveSymbol(n);
            return (
              <button
                key={n.id}
                type="button"
                data-testid={`notification-item-${n.id}`}
                onClick={() => openTrade(n)}
                disabled={!sym}
                className={`w-full text-left px-4 py-3 border-b border-white/5 transition-colors ${
                  sym ? "hover:bg-white/5 cursor-pointer" : "cursor-default"
                }`}
              >
                <div className="text-sm font-mono text-white">{n.title}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{n.body}</div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-[10px] text-zinc-600 font-mono">
                    {new Date(n.created_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}
                  </div>
                  {sym && (
                    <span className="text-[10px] font-mono text-blue-400">Vedi grafico →</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
